import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SA_PROVINCES, SOURCE_OF_FUNDS } from '../constants/saData';
import { getPublicConfig } from '../services/api';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Whether to surface the PEP self-declaration (SA compliance mode only).
  const [saCompliance, setSaCompliance] = useState(false);

  // ── Required fields ─────────────────────────────────────────────────────
  const [fullName, setFullName] = useState('');
  const [idType, setIdType] = useState<'sa_id' | 'passport'>('sa_id');
  const [idNumber, setIdNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [residentialAddress, setResidentialAddress] = useState('');

  // ── FICA fields ─────────────────────────────────────────────────────────
  const [province, setProvince] = useState('');
  const [occupation, setOccupation] = useState('');     // required
  const [sourceOfFunds, setSourceOfFunds] = useState(''); // required
  const [referralCode, setReferralCode] = useState('');

  // ── Consent checkboxes ──────────────────────────────────────────────────
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acknowledgeRG, setAcknowledgeRG] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isPep, setIsPep] = useState(false);

  // ── Errors / state ──────────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill referral code from ?ref= query param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setReferralCode(ref.toUpperCase().slice(0, 12));
  }, [searchParams]);

  // Load public flags (decides whether the PEP declaration is shown).
  useEffect(() => {
    getPublicConfig()
      .then((res) => setSaCompliance(!!res.data.sa_compliance_enabled))
      .catch(() => setSaCompliance(false));
  }, []);

  // ── Client-side validation ──────────────────────────────────────────────
  function validateForm(): Record<string, string> {
    const errors: Record<string, string> = {};

    if (fullName.trim().length < 3) {
      errors.fullName = 'Please enter your full legal name.';
    }
    if (idType === 'sa_id' && !/^\d{13}$/.test(idNumber.trim())) {
      errors.idNumber = 'SA ID number must be exactly 13 digits.';
    }
    if (idType === 'passport' && !/^[A-Za-z0-9]{6,20}$/.test(idNumber.trim())) {
      errors.idNumber = 'Passport number must be 6–20 alphanumeric characters.';
    }
    if (idType === 'passport' && !dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required for passport holders.';
    }
    if (mobileNumber.trim().length < 7) {
      errors.mobileNumber = 'Mobile number is required.';
    }
    if (residentialAddress.trim().length < 5) {
      errors.residentialAddress = 'Please enter your residential address.';
    }
    if (occupation.trim().length < 2) {
      errors.occupation = 'Please enter your occupation.';
    }
    if (!sourceOfFunds) {
      errors.sourceOfFunds = 'Please select your source of funds.';
    }
    if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }
    if (!acceptTerms) {
      errors.acceptTerms = 'You must confirm you are 18+ and accept the Terms.';
    }
    if (!acknowledgeRG) {
      errors.acknowledgeRG = 'You must acknowledge the Responsible Gambling notice.';
    }

    return errors;
  }

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setGeneralError(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        id_type: idType,
        id_number: idNumber.trim(),
        ...(idType === 'passport' ? { date_of_birth: dateOfBirth } : {}),
        mobile_number: mobileNumber.trim(),
        residential_address: residentialAddress.trim(),
        ...(province ? { province } : {}),
        occupation: occupation.trim(),
        source_of_funds: sourceOfFunds,
        accept_terms: acceptTerms,
        acknowledge_responsible_gambling: acknowledgeRG,
        marketing_opt_in: marketingOptIn,
        ...(saCompliance ? { is_pep: isPep } : {}),
        referral_code: referralCode.trim() || null,
      });
      // Account created + logged in — send them to verify their email before
      // they can fund/bet. The verification screen handles the code entry.
      navigate('/verify-email', { replace: true });
    } catch (err: unknown) {
      const anyErr = err as {
        response?: { status?: number; data?: { detail?: unknown } };
      };
      const httpStatus = anyErr?.response?.status;
      const detail = anyErr?.response?.data?.detail ?? '';
      const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);

      if (httpStatus === 403 || detailStr.includes('18 or older')) {
        // HARD 18+ age gate
        setFieldErrors({ idNumber: 'You must be 18 or older to register.' });
      } else if (detailStr.includes('Invalid ID') || detailStr.includes('Invalid passport')) {
        setFieldErrors({ idNumber: 'Please check your ID/passport number and try again.' });
      } else if (httpStatus === 409 && detailStr.toLowerCase().includes('id')) {
        setFieldErrors({
          idNumber: 'An account already exists for this ID. Try logging in instead.',
        });
      } else if (httpStatus === 409) {
        setFieldErrors({ email: 'An account with this email already exists.' });
      } else {
        setGeneralError(detailStr || 'Registration failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shared input / label classes ──────────────────────────────────────────
  const inputClass =
    'w-full min-h-[44px] rounded-button border border-border bg-surface px-4 py-3 text-text-primary placeholder-text-muted outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';
  const labelClass = 'mb-1 block text-sm font-medium text-text-primary';
  const errorClass = 'mt-1 text-xs text-error';
  const hintClass = 'mt-1 text-xs text-text-muted';

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <h1 className="mb-2 text-center font-heading text-4xl font-bold text-primary">
          🎯 My Bets
        </h1>
        <p className="mb-2 text-center text-sm text-text-muted">
          Create your account to start betting.
        </p>
        <p className="mb-6 text-center text-xs text-text-muted">
          As a licensed bookmaker we&apos;re required to verify your identity (FICA/KYC).
          You must be 18 or older.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* ── Full legal name ──────────────────────────────────────────── */}
          <div>
            <label htmlFor="fullName" className={labelClass}>
              Full legal name
            </label>
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.fullName}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={inputClass}
              placeholder="As it appears on your ID"
            />
            {fieldErrors.fullName && (
              <p className={errorClass} role="alert">{fieldErrors.fullName}</p>
            )}
          </div>

          {/* ── ID type ──────────────────────────────────────────────────── */}
          <div>
            <label htmlFor="idType" className={labelClass}>
              ID type
            </label>
            <select
              id="idType"
              value={idType}
              onChange={(e) => {
                setIdType(e.target.value as 'sa_id' | 'passport');
                setIdNumber('');
                setDateOfBirth('');
                setFieldErrors({});
              }}
              className={inputClass}
              aria-required="true"
            >
              <option value="sa_id">South African ID</option>
              <option value="passport">Passport</option>
            </select>
          </div>

          {/* ── ID number ────────────────────────────────────────────────── */}
          <div>
            <label htmlFor="idNumber" className={labelClass}>
              {idType === 'sa_id' ? 'SA ID number' : 'Passport number'}
            </label>
            <input
              id="idNumber"
              type="text"
              inputMode={idType === 'sa_id' ? 'numeric' : 'text'}
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.idNumber}
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              className={inputClass}
              placeholder={idType === 'sa_id' ? '13-digit SA ID number' : 'Passport number'}
            />
            {fieldErrors.idNumber && (
              <p className={errorClass} role="alert">{fieldErrors.idNumber}</p>
            )}
            <p className={hintClass}>
              Used to verify your identity and age — 18+ only. 🔒 Your ID is hashed,
              never stored in raw form (POPIA).
            </p>
          </div>

          {/* ── Date of birth (passport only) ────────────────────────────── */}
          {idType === 'passport' && (
            <div>
              <label htmlFor="dateOfBirth" className={labelClass}>
                Date of birth
              </label>
              <input
                id="dateOfBirth"
                type="date"
                required
                aria-required="true"
                aria-invalid={!!fieldErrors.dateOfBirth}
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className={inputClass}
              />
              {fieldErrors.dateOfBirth && (
                <p className={errorClass} role="alert">{fieldErrors.dateOfBirth}</p>
              )}
            </div>
          )}

          {/* ── Mobile number ────────────────────────────────────────────── */}
          <div>
            <label htmlFor="mobileNumber" className={labelClass}>
              Mobile number
            </label>
            <input
              id="mobileNumber"
              type="tel"
              autoComplete="tel"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.mobileNumber}
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              className={inputClass}
              placeholder="+27 82 123 4567"
            />
            {fieldErrors.mobileNumber && (
              <p className={errorClass} role="alert">{fieldErrors.mobileNumber}</p>
            )}
          </div>

          {/* ── Email ────────────────────────────────────────────────────── */}
          <div>
            <label htmlFor="email" className={labelClass}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@example.com"
            />
            {fieldErrors.email && (
              <p className={errorClass} role="alert">{fieldErrors.email}</p>
            )}
          </div>

          {/* ── Password ─────────────────────────────────────────────────── */}
          <div>
            <label htmlFor="password" className={labelClass}>
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.password}
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder="Min 8 characters"
            />
            {fieldErrors.password && (
              <p className={errorClass} role="alert">{fieldErrors.password}</p>
            )}
          </div>

          {/* ── Residential address ──────────────────────────────────────── */}
          <div>
            <label htmlFor="residentialAddress" className={labelClass}>
              Residential address
            </label>
            <textarea
              id="residentialAddress"
              autoComplete="street-address"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.residentialAddress}
              rows={2}
              value={residentialAddress}
              onChange={(e) => setResidentialAddress(e.target.value)}
              className={inputClass}
              placeholder="Street, suburb, city, postal code"
            />
            {fieldErrors.residentialAddress && (
              <p className={errorClass} role="alert">{fieldErrors.residentialAddress}</p>
            )}
          </div>

          {/* ── Province (optional) ──────────────────────────────────────── */}
          <div>
            <label htmlFor="province" className={labelClass}>
              Province <span className="font-normal text-text-muted">(optional)</span>
            </label>
            <select
              id="province"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className={inputClass}
            >
              <option value="">Select province</option>
              {SA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* ── Occupation (required, FICA) ──────────────────────────────── */}
          <div>
            <label htmlFor="occupation" className={labelClass}>
              Occupation
            </label>
            <input
              id="occupation"
              type="text"
              autoComplete="organization-title"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.occupation}
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              className={inputClass}
              placeholder="e.g. Accountant"
            />
            {fieldErrors.occupation && (
              <p className={errorClass} role="alert">{fieldErrors.occupation}</p>
            )}
          </div>

          {/* ── Source of funds (required, FICA AML) ─────────────────────── */}
          <div>
            <label htmlFor="sourceOfFunds" className={labelClass}>
              Source of funds
            </label>
            <select
              id="sourceOfFunds"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.sourceOfFunds}
              value={sourceOfFunds}
              onChange={(e) => setSourceOfFunds(e.target.value)}
              className={inputClass}
            >
              <option value="">Select source</option>
              {SOURCE_OF_FUNDS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {fieldErrors.sourceOfFunds && (
              <p className={errorClass} role="alert">{fieldErrors.sourceOfFunds}</p>
            )}
            <p className={hintClass}>
              Required by anti-money-laundering rules — tells us where your betting funds come from.
            </p>
          </div>

          {/* ── Referral code (optional) ─────────────────────────────────── */}
          <div>
            <label htmlFor="referralCode" className={labelClass}>
              Referral code <span className="font-normal text-text-muted">(optional)</span>
            </label>
            <input
              id="referralCode"
              type="text"
              autoComplete="off"
              maxLength={12}
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className={`${inputClass} font-mono uppercase`}
              placeholder="e.g. JAKE2024"
            />
            <p className={hintClass}>
              Have a friend&apos;s code? Enter it to reward them when you place your first bet.
            </p>
          </div>

          {/* ── Consent checkboxes ───────────────────────────────────────── */}
          <div className="space-y-3 rounded-card border border-border bg-surface px-4 py-4">
            <label className="flex items-start gap-3 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                aria-invalid={!!fieldErrors.acceptTerms}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-primary focus:ring-primary/30"
              />
              <span>
                I am 18 or older and accept the{' '}
                <Link to="/terms" className="font-semibold text-primary hover:underline" target="_blank">
                  Terms &amp; Betting Rules
                </Link>
                .
              </span>
            </label>
            {fieldErrors.acceptTerms && (
              <p className={errorClass} role="alert">{fieldErrors.acceptTerms}</p>
            )}

            <label className="flex items-start gap-3 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={acknowledgeRG}
                onChange={(e) => setAcknowledgeRG(e.target.checked)}
                aria-invalid={!!fieldErrors.acknowledgeRG}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-primary focus:ring-primary/30"
              />
              <span>
                I acknowledge the{' '}
                <Link to="/responsible-gambling" className="font-semibold text-primary hover:underline" target="_blank">
                  Responsible Gambling notice
                </Link>
                .
              </span>
            </label>
            {fieldErrors.acknowledgeRG && (
              <p className={errorClass} role="alert">{fieldErrors.acknowledgeRG}</p>
            )}

            {saCompliance && (
              <label className="flex items-start gap-3 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={isPep}
                  onChange={(e) => setIsPep(e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-primary focus:ring-primary/30"
                />
                <span>
                  I am a Politically Exposed Person (PEP), or a close associate/family member
                  of one. <span className="text-text-muted">(Leave unchecked if this does not apply.)</span>
                </span>
              </label>
            )}

            <label className="flex items-start gap-3 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-primary focus:ring-primary/30"
              />
              <span>Send me promotions (you can opt out anytime).</span>
            </label>
          </div>

          {/* ── General error ────────────────────────────────────────────── */}
          {generalError && (
            <div className="rounded-button bg-error/10 px-4 py-3 text-sm text-error" role="alert">
              {generalError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="cursor-pointer min-h-[44px] w-full rounded-button bg-primary py-3 font-bold text-white shadow-btn transition-colors hover:bg-primary-dark disabled:opacity-60"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-text-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-primary">
            Log in
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-text-muted">
          18+ only. Gamble responsibly — Helpline:{' '}
          <a href="tel:0800006008" className="font-semibold hover:underline">0800 006 008</a>
        </p>
      </div>
    </div>
  );
}
